export type NetworkTier = 'poor' | 'fair' | 'good' | 'excellent'

/** What the footer icon and the popover chip report. */
export type ConnectionStatus = NetworkTier | 'offline' | 'checking' | 'unknown'

export interface NetworkPing {
	internetMs?: number
	bytebeamMs?: number
}

const TIERS: NetworkTier[] = ['poor', 'fair', 'good', 'excellent']

export const isTier = (status: ConnectionStatus): status is NetworkTier => TIERS.includes(status as NetworkTier)

/** Lit bars out of four. */
export const tierBars = (tier: NetworkTier): number => TIERS.indexOf(tier) + 1

export const STATUS_LABEL: Record<ConnectionStatus, string> = {
	excellent: 'Excellent',
	good: 'Good',
	fair: 'Fair',
	poor: 'Poor',
	offline: 'Offline',
	checking: 'Checking',
	unknown: 'Not measured',
}

/** `text` for a value on a plain surface, `fill` for a meter segment, `chip` for a tinted label or notice. */
export const TONE = {
	success: { text: 'text-success', fill: 'bg-success', chip: 'bg-status-success-bg text-status-success-text' },
	warning: { text: 'text-warning', fill: 'bg-warning', chip: 'bg-status-warning-bg text-status-warning-text' },
	danger: { text: 'text-destructive', fill: 'bg-destructive', chip: 'bg-status-danger-bg text-status-danger-text' },
	muted: { text: 'text-muted-foreground', fill: 'bg-muted', chip: 'bg-muted text-muted-foreground' },
}

export const STATUS_TONE: Record<ConnectionStatus, (typeof TONE)[keyof typeof TONE]> = {
	excellent: TONE.success,
	good: TONE.success,
	fair: TONE.warning,
	poor: TONE.danger,
	offline: TONE.danger,
	checking: TONE.muted,
	unknown: TONE.muted,
}

// Breakpoints come from the AIM scoring in @cloudflare/speedtest (latency 50/100/500 ms, download 1/10/50 Mbps).
export const pingTier = (ms: number): NetworkTier => {
	if (ms < 50) {
		return 'excellent'
	}
	if (ms <= 100) {
		return 'good'
	}
	return ms <= 500 ? 'fair' : 'poor'
}

export const downloadTier = (bps: number): NetworkTier => {
	if (bps >= 50e6) {
		return 'excellent'
	}
	if (bps >= 10e6) {
		return 'good'
	}
	return bps >= 1e6 ? 'fair' : 'poor'
}

export const lowerTier = (a: NetworkTier, b: NetworkTier): NetworkTier =>
	TIERS[Math.min(TIERS.indexOf(a), TIERS.indexOf(b))]

/** Ping decides alone until a speed test has finished; then the weaker of ping and download wins. */
export const connectionStatus = (
	online: boolean,
	ping: NetworkPing | undefined,
	downloadBps?: number,
): ConnectionStatus => {
	if (!online) {
		return 'offline'
	}
	if (!ping) {
		return 'checking'
	}
	if (ping.internetMs === undefined) {
		return 'unknown'
	}
	const tier = pingTier(ping.internetMs)
	return downloadBps === undefined ? tier : lowerTier(tier, downloadTier(downloadBps))
}

/** Cloudflare reports its own processing time; left in, ping reads about 20 ms high. */
export const serverTimeMs = (header: string | null): number =>
	[...(header ?? '').matchAll(/cfSpeed\w*;\s*dur=([\d.]+)/gi)].reduce((sum, match) => sum + Number(match[1]), 0)
